import { useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import type { RequestDetail } from "@gw/shared-types";
import { parseRequestDetail } from "@/api/parsers";
import { BrandedLoader } from "@/components/BrandedLoader";
import { InfoTooltip } from "@/components/InfoTooltip";
import { useSetBreadcrumbs } from "@/components/shell/BreadcrumbsContext";
import { fmtDateOnly } from "@/lib/format";
import { AssistantPanel } from "@/features/assistant/AssistantPanel";
import { askAssistant } from "@/features/assistant/askAssistant";
import { useAssistantAccess } from "@/features/assistant/useAssistantAccess";
import { useFeatureFlags } from "@/config/useFeatureFlags";
import { AttentionBanner } from "@/features/requestDetail/components/AttentionBanner";
import { BackLink, parseDetailOrigin } from "@/features/requestDetail/components/BackLink";
import { ParticipantsSidebar } from "@/features/requestDetail/components/ParticipantsSidebar";
import { TasksTable, taskRowId } from "@/features/requestDetail/components/TasksTable";
import { attentionSummary } from "@/features/requestDetail/attentionNote";
import { groupTasks, taskGroupsSummary } from "@/features/requestDetail/taskGroups";
import { IDLE_TIP, taskTotals, TOTALS_TIP } from "@/features/requestDetail/taskTotals";
import { useSearchParams } from "react-router-dom";
import { JourneyHeader } from "./components/JourneyHeader";
import { LifecycleBar } from "./components/LifecycleBar";
import { RecordsRow } from "./components/RecordsRow";
import { StageTimingTable } from "./components/StageTimingTable";
import { journeyAttention } from "./journeyView";
import { useJourneyDetailQuery, type JourneyEntry } from "./useJourneyDetailQuery";

function Field({ label, value, tip }: { label: string; value: React.ReactNode; tip?: string }) {
  return (
    <div className="field">
      <div className="lbl">{label}{tip && <InfoTooltip inline text={tip} />}</div>
      <div className="val">{value ?? "—"}</div>
    </div>
  );
}

/**
 * One consultant journey on one page (Rafael, 2026-10-21): the intake, the pre-verification, the work
 * order and the request together, with the lifecycle bar, the attention callout, the tasks, the
 * stage timing against peers, the participants, every record of the journey and the details folded
 * at the bottom. Mounted on /requests/:id (any request) and /intake/:source/:id (an intake).
 */
export function JourneyDetailScreen() {
  const { id, source } = useParams();
  const [searchParams] = useSearchParams();
  const entry: JourneyEntry | null = id ? (source ? { kind: "intake", source, id } : { kind: "request", id }) : null;
  const { data, isLoading, isError, error } = useJourneyDetailQuery(entry);
  const flags = useFeatureFlags();
  const assistantAccess = useAssistantAccess();
  const [tasksOpen, setTasksOpen] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);

  const request = useMemo<RequestDetail | null>(() => (data?.request ? parseRequestDetail(data.request) : null), [data]);
  const summary = useMemo(() => attentionSummary(data?.requestAttention), [data]);
  const sittingTaskInTable = summary?.task?.id != null && !!request?.tasks.some((t) => t.taskId === summary.task!.id);
  const origin = parseDetailOrigin(searchParams.get("via"));

  useSetBreadcrumbs(
    data
      ? [
          origin === "operations" ? { label: "Operations", to: "/operations" } : { label: "Requests", to: "/requests" },
          { label: data.worker ?? "Journey" },
        ]
      : [],
  );

  const showTasks = () => {
    setTasksOpen(true);
    window.setTimeout(() => {
      const taskId = summary?.task?.id;
      const row = taskId ? document.getElementById(taskRowId(taskId)) : document.getElementById("journey-tasks");
      row?.scrollIntoView({ behavior: "smooth", block: "center" });
      if (taskId) row?.focus();
    }, 0);
  };
  const askWhy = () => {
    const attention = data ? journeyAttention(data) : null;
    const what = attention && (attention.state === "stuck" || attention.state === "blocked" || attention.state === "stuckAndBlocked") ? (attention.state === "stuckAndBlocked" ? "stuck and blocked" : attention.state) : "where it is";
    askAssistant(`Why is this journey ${what} on the Operations dashboard, and what should happen next?`);
  };
  const canAsk = flags.operationsAssistant && assistantAccess.allowed;

  if (isLoading) {
    return (
      <main className="screen request-detail" aria-busy="true">
        <BackLink />
        <div className="screen-body" style={{ minHeight: "60vh" }}><BrandedLoader /></div>
      </main>
    );
  }
  if (isError || !data) {
    return (
      <main className="screen request-detail">
        <BackLink />
        <div className="empty">
          <h4>{source ? "Intake not found" : "Request not found"}</h4>
          <p>{(error as Error)?.message ?? "This ID may have been removed."}</p>
        </div>
      </main>
    );
  }

  const totals = request ? taskTotals(request.tasks) : null;
  const wo = data.workOrder;

  return (
    <>
      <BackLink />
      <JourneyHeader journey={data} onAskClaude={canAsk ? askWhy : undefined} />

      {data.note && <div className="journey-note" role="status">{data.note}</div>}

      <div className="section">
        <LifecycleBar journey={data} onShowTasks={request ? showTasks : undefined} />
        {summary && (
          <AttentionBanner summary={summary} onShowTask={sittingTaskInTable ? showTasks : undefined} onAskClaude={canAsk ? askWhy : undefined} />
        )}
        {!summary && data.intakeAttention && data.intakeAttention.state !== "clear" && (
          <section className={`att-banner ${data.intakeAttention.state}`} data-testid="intake-attention-banner">
            <div>
              <div className="att-banner-why">
                The intake carries this journey and is <b>{data.intakeAttention.state === "stuckAndBlocked" ? "Stuck + Blocked" : data.intakeAttention.state === "stuck" ? "Stuck" : "Blocked"}</b>: {data.intakeAttention.reason}.
                {data.carrier === "awaitingRequest" ? " The work order exists; the journey waits for its request." : " Nothing exists in FBO yet."}
              </div>
              <dl className="att-banner-facts">
                <div><dt>Waiting</dt><dd>{data.intakeAttention.waitingBd} bd since the intake started</dd></div>
                <div><dt>Untouched</dt><dd>{data.intakeAttention.untouchedBd} bd</dd></div>
                <div><dt>Status unchanged</dt><dd>{data.intakeAttention.sinceStatusBd} bd</dd></div>
                <div><dt>Limits</dt><dd>touch {data.stuckAfterBd} bd · move {data.blockedAfterBd} bd</dd></div>
              </dl>
            </div>
            {canAsk && (
              <div className="att-banner-actions">
                <button type="button" className="btn btn-ghost btn-sm" onClick={askWhy}><span className="material-symbols-rounded">forum</span>Ask Claude why</button>
              </div>
            )}
          </section>
        )}
      </div>

      {request && (
        <div className="section" id="journey-tasks">
          <button type="button" className="fold" aria-expanded={tasksOpen} onClick={() => setTasksOpen((v) => !v)}>
            <h3>
              Tasks in Request processing{" "}
              <span className="sub">
                {request.tasks.length} tasks
                {" · "}{taskGroupsSummary(groupTasks(request.tasks, summary?.task?.id ?? undefined))}
                {data.primaryRequestId ? " · of the pre-verification request" : ""}
              </span>
            </h3>
            <span className="tg">{tasksOpen ? "Hide ▴" : "Show ▾"}</span>
          </button>
          {tasksOpen && (
            <TasksTable
              tasks={request.tasks}
              unit="bd"
              attention={
                summary?.task?.id && sittingTaskInTable && summary.businessDays != null
                  ? { sittingTaskId: summary.task.id, sittingBd: summary.businessDays, state: summary.state, queuedTaskIds: summary.queuedTaskIds }
                  : undefined
              }
            />
          )}
        </div>
      )}

      <div className={`detail-grid${request ? "" : " full"}`}>
        <StageTimingTable journey={data} />
        {request && <ParticipantsSidebar request={request} />}
      </div>

      <RecordsRow journey={data} />

      <div className="section">
        <button type="button" className="fold" aria-expanded={detailsOpen} onClick={() => setDetailsOpen((v) => !v)}>
          <h3>Details <span className="sub">client, work order, task time totals and IDs</span></h3>
          <span className="tg">{detailsOpen ? "Hide ▴" : "Show ▾"}</span>
        </button>
        {detailsOpen && (
          <div className="detail-header fields-only">
            <div className="row2">
              <Field label="Client" value={data.client} />
              <Field label="Line of business" value={request?.lineOfBusiness ?? null} />
              <Field label="Engagement" value={request?.engagementType ?? null} />
              <Field label="Internal company" value={request?.internalCompany ?? null} />
              <Field label="Assigned to" value={request?.assignedToUserName ?? data.intake?.assignedToName ?? null} />
              <Field label="Worker location" value={request ? [request.workerLocationProvince, request.workerLocationCountry].filter(Boolean).join(", ") : null} />
              {totals && (
                <>
                  <Field label="Task idle" value={`${totals.idleBd.toFixed(2)} bd`} tip={IDLE_TIP} />
                  <Field label="Task wait" value={`${totals.waitBd.toFixed(2)} bd`} tip={TOTALS_TIP} />
                  <Field label="Task execution" value={`${totals.execBd.toFixed(2)} bd`} />
                  <Field label="Task duration" value={`${(totals.idleBd + totals.waitBd + totals.execBd).toFixed(2)} bd`} />
                </>
              )}
              {wo && (
                <>
                  <Field label="WO submit count" value={String(wo.submitCount)} />
                  <Field label="WO reject count" value={String(wo.rejectCount)} />
                  <Field label="WO approver" value={wo.approver} />
                  <Field label="WO start → end" value={wo.startDate ? `${fmtDateOnly(wo.startDate)} → ${fmtDateOnly(wo.endDate)}` : null} />
                </>
              )}
              <Field label="Tenant" value={request?.tenantName ?? "Procom"} />
              {request && <Field label="Request ID" value={request.id} />}
              {data.intake && <Field label="Intake ID" value={data.intake.id} />}
              {data.preOnboarding && <Field label="Pre-Verification ID" value={data.preOnboarding.id} />}
            </div>
          </div>
        )}
      </div>

      <AssistantPanel
        screen="requestDetail"
        activeFilters={{ requestId: data.request?.id ?? id }}
        context={{ scope: data.worker ? `${data.worker}'s journey` : "This journey", recordId: data.request?.id ?? id }}
        floatingCollapsed
      />
    </>
  );
}
